import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Emergent Auth returns session_id in the URL fragment (#session_id=...), not as a query param.

function parseSessionIdFromHash() {
  const hash = window.location.hash || '';
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(trimmed);
  return params.get('session_id');
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { googleLogin } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = parseSessionIdFromHash();
    if (!sessionId) {
      setError('No session ID found. Please try again.');
      return;
    }

    (async () => {
      const result = await googleLogin(sessionId);
      // Clear the fragment so session_id doesn't linger in URL
      window.history.replaceState(null, '', window.location.pathname);
      if (result.success) {
        navigate('/dashboard', { replace: true });
      } else {
        setError(result.error || 'Google login failed. Please try again.');
      }
    })();
  }, [googleLogin, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-red-600 mb-4" data-testid="auth-callback-error">{error}</p>
          <a href="/login" className="text-[#FB923C] font-bold hover:text-orange-600">Back to login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#FB923C] mx-auto mb-4" />
        <p className="text-slate-600" data-testid="auth-callback-loading">Signing you in with Google...</p>
      </div>
    </div>
  );
}
